import gql from 'graphql-tag'

export const TaskStatusOptionsQuery = gql`query TaskStatusOptionsQuery {
    optionsList: __type(name: "TaskRabbitTaskRabbitStatusChoices") {
        enumValues {
            name
            description
        }
    }
}`

export const BoardQuery = gql`query Board($id: ID!){
    board(id: $id){
        id
        pk
        title
        description
        isPrivate
        dateAdded
        owner {
            id
            username
            firstName
            lastName
        }
        sharedWithGroups {edges{node{id name}}}
        sharedWithUsers{edges{node{id pk name firstName lastName username}}}
        columnSet {
            edges {
                node {
                    id
                    pk
                    title
                    isArchived
                    isRequired
                    columnType
                    allowedOptions
                    stateTransitions
                    mirrorPath
                    contentType {
                        id
                        model
                    }
                }
            }
        }
    }
}`

export const AllColumnsQuery = gql`query AllColumnsQuery($boardId: ID!){
    allColumns(boardId: $boardId, isArchived: false){
        edges {
            node {
                id
                pk
                title
                isArchived
                isRequired
                columnType
                allowedOptions
                stateTransitions
                mirrorPath
                contentType {
                    id
                    appLabel
                    model
                }
            }
        }
    }
}`

export const AllBoardsQuery = gql`query AllBoards($cursor: String, $orderBy:[String], $first: Int, $searchTerm: String){
    allBoards(after: $cursor, orderBy: $orderBy, first: $first, isArchived: false, title_Icontains: $searchTerm){
        edges{
            cursor
            node{
                id
                pk
                title
                description
                dateAdded
                isArchived
                isPrivate
                owner {
                    id
                    username
                    firstName
                    lastName
                }
            }
        }
        pageInfo{
            hasNextPage
            endCursor
            length
        }
    }

}`

// The board sidebar deliberately omits columnSet: nesting it costs one query per board
// server-side, and only the active board's columns are ever used. The task list fetches
// those itself via AllColumnsQuery.
//
// BoardTypeAhead is the one caller that genuinely needs columns across boards -- it filters
// the dropdown down to boards holding a column of a given content type -- so it gets this
// narrow query instead of the full column payload.
export const BoardOptionsQuery = gql`query BoardOptions($searchTerm: String){
    allBoards(isArchived: false, title_Icontains: $searchTerm){
        edges{
            node{
                id
                title
                columnSet {
                    edges {
                        node {
                            id
                            contentType {
                                id
                                model
                            }
                        }
                    }
                }
            }
        }
    }
}`


// $includeColumnValues defaults to true so existing callers (kanban, driver tasks tab) are
// unaffected. The task list passes false and fetches column values afterwards, so the table
// paints without waiting on the per-row custom-column work -- see
// buildDeferredTaskColumnValuesQuery below.
// $includeRelatedObjects likewise defaults to false: only the My Tasks list renders
// the aggregate Related Objects column, and requesting the field is what triggers the
// server-side FK prefetch. Leaving it false keeps board views and kanban unchanged.
export const AllTasksQuery = gql`query AllTasks($orderBy:[String], $first: Int, $after: String, $searchTerm: String, $incidentId: ID, $driverId: ID, $filters: [FilterInput], $includeColumnValues: Boolean! = true, $includeRelatedObjects: Boolean! = false){
    allTasks(orderBy: $orderBy, first: $first, after: $after, searchTerm: $searchTerm, incidentId: $incidentId, driverId: $driverId, filters: $filters){
        edges{
            node{
                id
                pk
                dateAdded
                dateModified
                title
                description
                dueDate
                status
                commentsCount
                isArchived
                isAutomated
                taskcolumnvalueSet @include(if: $includeColumnValues) {
                    edges {
                        node {
                            id
                            value
                            grapheneId
                            valueString
                            column {
                                id
                                pk
                                title
                                columnType
                                contentType {
                                    id
                                    model
                                }
                            }
                        }
                    }
                }
                relatedObjects @include(if: $includeRelatedObjects) {
                    columnId
                    columnTitle
                    model
                    value
                    grapheneId
                    valueString
                }
                assignedTo{
                    edges {
                      node { 
                            id 
                            pk
                            username
                            firstName
                            lastName
                        }
                    }
                }
                group {
                    id
                    name
                }
                board{
                    id
                    title
                }
                createdBy {
                    id
                    username
                    firstName
                    lastName
                }
            }
        }
        pageInfo{
            hasNextPage
            endCursor
            length
        }
    }
    
}`


// Custom column values are the expensive part of a task row: the server resolves them per
// task, dereferencing a generic FK for each one. Fetching them in a second pass lets the
// table render immediately and fill the custom columns in after.
//
// One aliased `task(id:)` selection per row, matching the approach used by the incidents
// listing (`buildDeferredIncidentFieldsQuery` in Incidents/IncidentsList.js). Returns null
// for an empty list so callers can skip the request entirely.
export const buildDeferredTaskColumnValuesQuery = (taskIds) => {
    if (!taskIds || !taskIds.length) {
        return null
    }

    const variableDefinitions = taskIds.map((_, index) => `$taskId${index}: ID!`).join(", ")
    const taskSelections = taskIds
        .map((_, index) => `
        task_${index}: task(id: $taskId${index}) {
            id
            taskcolumnvalueSet {
                edges {
                    node {
                        id
                        value
                        grapheneId
                        valueString
                        column {
                            id
                            pk
                            title
                            columnType
                            contentType {
                                id
                                model
                            }
                        }
                    }
                }
            }
        }
`)
        .join("\n")

    return gql`
        query DeferredTaskColumnValues(${variableDefinitions}) {
            ${taskSelections}
        }
    `
}

export const TaskQuery = gql`query Task($id: ID!){
    task(id: $id){
        id
        title
        isAutomated
        description
        dateAdded
        dueDate
        status
        createdBy {id username firstName lastName }
        assignedTo {
            edges {
                node {
                    id
                    pk
                    username
                    firstName
                    lastName
                }
            }
        }
        group {
            id
            name
        }
        board {
            id
            title
            columnSet {
                edges {
                    node {
                        id
                        pk
                        title
                        isArchived
                        columnType
                        allowedOptions
                        contentType {
                            id
                            model
                        }
                    }
                }
            }
        }
        taskcolumnvalueSet {
            edges {
                node {
                    id
                    value
                    valueString
                    grapheneId
                    column {
                        id
                        pk
                        columnType
                        contentType {
                            id
                            model
                        }
                    }
                }
            }
        }
        taskchecklistSet {
            edges {
                node {
                    id
                    title
                    description
                    mustComplete
                    completedOn
                }
          }
        }
    }
}`


export const AllDriversQuery = gql`query AllDrivers($searchTerm: String){
    allDrivers(searchTerm: $searchTerm, first: 10){
        edges{
            node{ 
                id pk name branch{ id name }
            }
        }
    }
    
}`

export const RepsQuery = gql`
     query AllRepsQuery($groups:[String], $searchTerm: String, $permissions: [String], $boardId: ID){
        allReps(searchTerm: $searchTerm, first: 10, isStaff:true, isActive:true, groups:$groups, orderBy:["username"], permissions: $permissions, boardId: $boardId){
        edges{
          node{
            id
            name
            username
            firstName
            lastName
          }
        }
      }
    }
`

export const GroupsQuery = gql`
    query AllGroupsQuery($searchTerm: String, $boardId: ID){
        allGroups(name_Icontains: $searchTerm, first: 10, boardId: $boardId){
            edges{
                node{
                    id
                    name
                }
            }
        }
    }
`

export const AllTaskCommentReactionsQuery = gql`query AllTaskCommentReactions($commentId: ID, $taskId: ID, $createdById: ID){
    allTaskCommentReactions(commentId: $commentId, taskId: $taskId, createdById: $createdById){
        edges{
            node{
                id
                reaction
                comment{ id }
                createdBy{
                    id
                    username
                    firstName
                    lastName
                }
            }
        }
    }
}`

export const AllTaskCommentsQuery = gql`query allTaskComments($taskId: ID!) {
    allTaskComments(taskId: $taskId, isArchived: false) {
      edges {
        node {
          id
          dateAdded
          parentComment {
            id
            createdBy {
              id
              username
              firstName
              lastName
            }
          }
          createdBy {
            id
            username
            firstName
            lastName
          }
          comment
          reactionsCount
          isArchived
          taskcommentattachmentSet {
            edges {
              node {
                id
                url
                extension
                name
              }
            }
          }
        }
      }
    }
  }
`
